import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import './ProjectView.css';
import FileExplorer from './FileExplorer/FileExplorer';
import { getVirtualTourUrl, getVirtualTourCode } from '../../../utils/virtualTourMapping';
import fileService from '../../../services/fileService';
import viewerPresenceService from '../../../services/viewerPresenceService';
import ManageProjectUsersModal from '../ProjectsList/ProjectCard/ManageProjectUsersModal';
import LeaveProjectModal from '../ProjectsList/ProjectCard/LeaveProjectModal';
import RenameProjectModal from '../ProjectsList/ProjectCard/RenameProjectModal';
import RSAModelAnalysis from '../../Sidebar/RSAAnalysis/RSAModelAnalysis/RSAModelAnalysis';
import RSAExistingAnalysis from '../../Sidebar/RSAAnalysis/RSAExistingAnalysis/RSAExistingAnalysis';
import PermissionGate from '../../PermissionGate/PermissionGate';
import { API_BASE_URL, ENVIRONMENT } from '../../../API/api';

const RSA_VIEW_QUERY_KEY = 'rsa_view';
const RSA_TAB_QUERY_KEY = 'rsa_tab';

const getRsaViewModeFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  const rawView = String(params.get(RSA_VIEW_QUERY_KEY) || '').trim().toLowerCase();
  return rawView === 'analysis' ? 'analysis' : 'files';
};

const getRsaTabFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  return params.get(RSA_TAB_QUERY_KEY) || null;
};

const ProjectView = ({ project, projectData, onBack, onViewProject }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isRsaProject =
    project?.ProjectType === 'rsa-existing' || project?.ProjectType === 'rsa-model';
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const folderId = params.get('folder');
    return folderId ? { id: folderId, name: '' } : null;
  });
  const [currentPath, setCurrentPath] = useState([{ id: 'root', name: project?.Name || 'Project Files' }]);
  const [hasSelectedFiles, setHasSelectedFiles] = useState(false);
  const [canDeleteSelection, setCanDeleteSelection] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showLeaveProjectModal, setShowLeaveProjectModal] = useState(false);
  const [showAnalysisFlyout, setShowAnalysisFlyout] = useState(false);
  const [flyoutFixedStyle, setFlyoutFixedStyle] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [displayProjectName, setDisplayProjectName] = useState(project?.Name || 'Project Files');
  const [rsaViewMode, setRsaViewMode] = useState(() => {
    if (!isRsaProject) return 'files';
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    const fromUrl = getRsaViewModeFromSearch(search);
    // Both RSA model and rsa-existing default to analysis view
    if (fromUrl === 'analysis') return 'analysis';
    // ?folder=X without explicit rsa_view=analysis means the user wants files view
    if (params.get('folder') && !params.get(RSA_VIEW_QUERY_KEY)) return 'files';
    if (project?.ProjectType === 'rsa-model' || project?.ProjectType === 'rsa-existing') return 'analysis';
    return fromUrl;
  }); // 'files' | 'analysis' for RSA projects
  const [preloadedRsaSetup, setPreloadedRsaSetup] = useState(null);
  const [preloadedRsaSetupFolderId, setPreloadedRsaSetupFolderId] = useState(null);
  const [preloadedRsaSetupFolderName, setPreloadedRsaSetupFolderName] = useState(null);
  const [preloadRsaSetupLoading, setPreloadRsaSetupLoading] = useState(false);
  const [rsaResyncLoading, setRsaResyncLoading] = useState(false);
  const [isLeavingProject, setIsLeavingProject] = useState(false);
  const fileExplorerRef = useRef(null);
  const headerMenuRef = useRef(null);
  const flyoutTimerRef = useRef(null);
  const analysisBtnRef = useRef(null);
  const routeRsaTab = getRsaTabFromSearch(location.search);
  const currentUserEmail = (
    sessionStorage.getItem('userEmail')
    || localStorage.getItem('userEmail')
    || ''
  ).trim().toLowerCase();

  const updateRsaRouteState = React.useCallback((nextViewMode, nextTab = null, nextFolderId = null) => {
    if (!project?.Id) {
      return;
    }
    const params = new URLSearchParams(location.search);
    if (nextViewMode === 'analysis') {
      params.set(RSA_VIEW_QUERY_KEY, 'analysis');
      if (nextTab) {
        params.set(RSA_TAB_QUERY_KEY, nextTab);
      } else {
        params.delete(RSA_TAB_QUERY_KEY);
      }
    } else {
      params.set(RSA_VIEW_QUERY_KEY, 'files');
      params.delete(RSA_TAB_QUERY_KEY);
    }
    if (nextFolderId) {
      params.set('folder', nextFolderId);
    } else if (nextViewMode === 'analysis') {
      params.delete('folder');
    }
    const search = params.toString();
    navigate(
      {
        pathname: `/projects/${project.Id}`,
        search: search ? `?${search}` : '',
      },
      { replace: true }
    );
  }, [location.search, navigate, project?.Id]);

  const handleActiveMainTabChange = React.useCallback((nextTab) => {
    updateRsaRouteState('analysis', nextTab);
  }, [updateRsaRouteState]);

  const handleGoToFiles = React.useCallback((targetFolder = null) => {
    setRsaViewMode('files');
    updateRsaRouteState('files', null, targetFolder?.id || null);
    setSelectedFile(null);
    if (targetFolder?.id && project?.Id) {
      const nextFolder = {
        id: targetFolder.id,
        name: targetFolder.name || 'Deliverables',
      };
      setSelectedFolder(nextFolder);
    }
    const flagKey = 'rsa_analysis_back_flyout_shown';
    if (!sessionStorage.getItem(flagKey)) {
      sessionStorage.setItem(flagKey, '1');
      const rect = analysisBtnRef.current?.getBoundingClientRect();
      if (rect) {
        setFlyoutFixedStyle({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 6,
        });
      }
      setShowAnalysisFlyout(true);
      if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
      flyoutTimerRef.current = setTimeout(() => setShowAnalysisFlyout(false), 4000);
    }
  }, [project?.Id, updateRsaRouteState]);

  useEffect(() => {
    if (!isRsaProject) {
      if (rsaViewMode !== 'files') {
        setRsaViewMode('files');
      }
      return;
    }
    const nextViewMode = getRsaViewModeFromSearch(location.search);
    if (nextViewMode !== rsaViewMode) {
      setRsaViewMode(nextViewMode);
    }
  }, [isRsaProject, location.search, rsaViewMode]);

  // Preload RSA setup from central registry (Shared Drive) when project page loads (RSA project types)
  useEffect(() => {
    if (!project || !isRsaProject || !project.Id) {
      setPreloadedRsaSetup(null);
      setPreloadedRsaSetupFolderId(null);
      setPreloadedRsaSetupFolderName(null);
      setPreloadRsaSetupLoading(false);
      return;
    }
    const projectId = String(project.Id);
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const apiBase = baseUrl ? `${API_BASE_URL}/api/google-drive/rsa` : '/api/google-drive/rsa';
    setPreloadRsaSetupLoading(true);
    axios.get(`${apiBase}/registry/setup`, { params: { project_id: projectId } })
      .then((res) => {
        const { setup, folderId, folderName } = res.data || {};
        setPreloadedRsaSetup(setup && typeof setup === 'object' ? setup : null);
        setPreloadedRsaSetupFolderId(folderId || null);
        setPreloadedRsaSetupFolderName(folderName || null);
      })
      .catch(() => {
        setPreloadedRsaSetup(null);
        setPreloadedRsaSetupFolderId(null);
        setPreloadedRsaSetupFolderName(null);
      })
      .finally(() => setPreloadRsaSetupLoading(false));
  }, [project?.Id, project?.ProjectType]);

  // Update breadcrumb when project changes
  useEffect(() => {
    if (project) {
      const nextName = project.Name || 'Project Files';
      setDisplayProjectName(nextName);
      setCurrentPath([{ id: 'root', name: nextName }]);
    }
  }, [project]);

  useEffect(() => {
    if (!showHeaderMenu) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setShowHeaderMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHeaderMenu]);

  useEffect(() => {
    return () => {
      if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
    };
  }, []);

  // Check for selected files periodically to update download button visibility
  useEffect(() => {
    const checkSelectedFiles = () => {
      const fe = fileExplorerRef.current;
      if (fe && fe.hasSelectedFiles !== undefined) {
        setHasSelectedFiles(fe.hasSelectedFiles);
        setCanDeleteSelection(Boolean(fe.canDeleteSelection));
      }
    };

    // Check immediately and then periodically
    checkSelectedFiles();
    const interval = setInterval(checkSelectedFiles, 100);

    return () => clearInterval(interval);
  }, []);

  const updateBreadcrumb = (newPath) => {
    setCurrentPath((prevPath) => {
      if (!Array.isArray(newPath)) return prevPath;
      if (
        prevPath.length === newPath.length &&
        prevPath.every((item, index) => item?.id === newPath[index]?.id && item?.name === newPath[index]?.name)
      ) {
        return prevPath;
      }
      return newPath;
    });
  };

  if (!project) {
    return (
      <div className="project-view-container">
        <div className="project-view-error">
          <h2>Project not found</h2>
          <button onClick={onBack}>Back to Projects</button>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Convert to Date object if it's a string
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return 'N/A';
    
    return dateObj.toLocaleDateString() + ' ' + 
           dateObj.getHours().toString().padStart(2, '0') + ':' + 
           dateObj.getMinutes().toString().padStart(2, '0');
  };

  const isExpired = project.Expiration && new Date(project.Expiration) <= new Date();
  const canView = project.Viewable && !isExpired;
  const hasVirtualTour = project.Code && project.Code !== '';

  const handleFileSelect = (file) => {
    console.log('📁 File selected:', file);
    setSelectedFile(file);
    setSelectedFolder(null);
  };

  const handleFolderSelect = (folder) => {
    console.log('📁 Folder selected:', folder);
    setSelectedFolder(folder);
    setSelectedFile(null);
  };

  const handleBreadcrumbClick = (index, event) => {
    event.stopPropagation(); // Prevent clearing selection when clicking breadcrumbs
    // Navigate to the clicked breadcrumb level
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    
    // If clicking on root (index 0), navigate to root folder
    if (index === 0) {
      setSelectedFolder(null);
      // Update URL to project root
      const url = `/projects/${project.Id}`;
      const state = { folderId: 'root' };
      window.history.pushState(state, '', url);
    } else {
      // Find the folder corresponding to the clicked breadcrumb item
      const targetFolderId = newPath[newPath.length - 1].id;
      // We'll need to pass this to FileExplorer to handle the navigation
      setSelectedFolder({ id: targetFolderId, name: newPath[newPath.length - 1].name });
    }
  };

  const handleClearSelection = () => {
    // This will be passed to FileExplorer to clear file selections
    // The actual implementation is in FileExplorer component
  };

  const handleRefresh = () => {
    // Refresh files via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleRefresh) {
      fileExplorerRef.current.handleRefresh();
    }
  };

  const handleRsaResyncSourceCaches = async (e) => {
    e.stopPropagation();
    if (!project?.Id || rsaResyncLoading) {
      return;
    }
    setRsaResyncLoading(true);
    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      const url = baseUrl
        ? `${API_BASE_URL}/api/projects/${project.Id}/rsa-resync-source-caches`
        : `/api/projects/${project.Id}/rsa-resync-source-caches`;
      const res = await axios.post(url);
      const d = res.data || {};
      const processed = d.xml_files_processed ?? 0;
      const folderCount = Array.isArray(d.rsa_folders) ? d.rsa_folders.length : 0;
      const errCount = d.error_count ?? 0;
      let msg = `RSA caches resynced: ${processed} XML file(s), ${folderCount} RSA folder(s) under Source.`;
      if (errCount > 0) {
        msg += ` ${errCount} issue(s) reported — check server logs or response payload.`;
      }
      try {
        sessionStorage.setItem(
          `rsaMileageCachesSynced:${project.Id}`,
          String(Date.now())
        );
      } catch (e) {
        /* ignore quota / private mode */
      }
      window.alert(msg);
      window.dispatchEvent(
        new CustomEvent('rsa-mileage-caches-synced', { detail: { projectId: project.Id } })
      );
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        window.alert(
          'RSA resync endpoint was not found (404). Deploy the latest Backend/123bimlive (routes/project_routes.py) to this API host, or use the same base URL as /api/projects/.../files. After deploy, GET the same path should return JSON with endpoint info instead of 404.'
        );
        return;
      }
      const apiMsg = err.response?.data?.error || err.response?.data?.hint;
      window.alert(apiMsg || err.message || 'RSA cache resync failed.');
    } finally {
      setRsaResyncLoading(false);
    }
  };

  const handleRenameProject = async (newName) => {
    const trimmedName = String(newName || '').trim();
    if (!trimmedName || !project?.Id) {
      return;
    }

    setRenameError('');
    setIsRenaming(true);

    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/projects/${project.Id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmedName })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }

      if (!response.ok || payload?.success === false) {
        const backendError = payload?.error || payload?.message;
        throw new Error(backendError || 'Failed to rename project');
      }

      setDisplayProjectName(trimmedName);
      setCurrentPath((prevPath) => {
        if (!Array.isArray(prevPath) || prevPath.length === 0) {
          return [{ id: 'root', name: trimmedName }];
        }
        return prevPath.map((item, index) => (
          index === 0 ? { ...item, name: trimmedName } : item
        ));
      });
      setShowRenameModal(false);
      setRenameError('');
    } catch (error) {
      console.error(`Error renaming project ${project.Id}:`, error);
      setRenameError(error.message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleHeaderMenuAction = (action, event) => {
    event?.stopPropagation?.();
    setShowHeaderMenu(false);

    switch (action) {
      case 'manageUsers':
        setShowManageUsersModal(true);
        break;
      case 'rename':
        setRenameError('');
        setShowRenameModal(true);
        break;
      case 'leave':
        setShowLeaveProjectModal(true);
        break;
      default:
        break;
    }
  };

  const handleLeaveProject = async () => {
    if (!project?.Id) {
      return;
    }
    if (!currentUserEmail) {
      window.alert('Could not determine the current user email.');
      return;
    }
    if (isLeavingProject) {
      return;
    }

    setIsLeavingProject(true);
    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/projects/${project.Id}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: currentUserEmail })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }

      if (!response.ok || payload?.success === false) {
        const backendError = payload?.error || payload?.message;
        throw new Error(backendError || 'Failed to leave project');
      }

      setShowLeaveProjectModal(false);
      onBack?.();
    } catch (error) {
      console.error(`Error leaving project ${project.Id}:`, error);
      window.alert(error.message || 'Failed to leave project.');
    } finally {
      setIsLeavingProject(false);
    }
  };

  const headerMenuButton = (
    <div className="project-view-header-menu" ref={headerMenuRef}>
      <button
        className="menu-trigger-header"
        onClick={(e) => {
          e.stopPropagation();
          setShowHeaderMenu((prev) => !prev);
        }}
        title="Project menu"
        aria-haspopup="menu"
        aria-expanded={showHeaderMenu ? 'true' : 'false'}
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>
      {showHeaderMenu && (
        <div className="project-menu-dropdown project-view-menu-dropdown">
          <ul className="project-menu-list" role="menu">
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('manageUsers', e)} role="menuitem">
                <i className="fas fa-users-cog"></i>
                Manage Project Users
              </button>
            </li>
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('rename', e)} role="menuitem">
                <i className="fas fa-edit"></i>
                Rename Project
              </button>
            </li>
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('leave', e)} role="menuitem" className="danger">
                <i className="fas fa-sign-out-alt"></i>
                Leave Project
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );

  const handleDownload = () => {
    // Trigger download via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleDownload) {
      fileExplorerRef.current.handleDownload();
    }
  };

  const handleExplorerDelete = () => {
    if (fileExplorerRef.current?.handleDelete) {
      fileExplorerRef.current.handleDelete();
    }
  };

  const handleNewFolder = async () => {
    const currentFolderContext =
      fileExplorerRef.current?.getCurrentFolderContext?.() || null;
    const rawParentId = currentFolderContext?.id;
    const parentId = Number.isInteger(rawParentId)
      ? rawParentId
      : (
          typeof rawParentId === 'number' && Number.isFinite(rawParentId)
            ? rawParentId
            : project.Id
        );
    const parentGoogleDriveId = currentFolderContext?.googleDriveId || null;
    
    // Prompt user for folder name
    const folderName = prompt('Enter folder name:');
    if (!folderName || !folderName.trim()) {
      return; // User cancelled or entered empty name
    }
    
    try {
      // Create the folder
      const response = await fileService.createFolder(
        project.Id,
        folderName.trim(),
        parentId,
        parentGoogleDriveId
      );
      
      // Add folder to current view without full refresh
      const folderForExplorer = response.drive_item || response.folder;
      if (folderForExplorer && fileExplorerRef.current && fileExplorerRef.current.addFolderToCurrentFolder) {
        fileExplorerRef.current.addFolderToCurrentFolder(folderForExplorer);
      } else {
        // Fallback to refresh if add method not available
        handleRefresh();
      }
    } catch (error) {
      alert(`Error creating folder: ${error.message}`);
      console.error('Error creating folder:', error);
    }
  };

  const previousBreadcrumbStep = currentPath.length > 1
    ? currentPath[currentPath.length - 2]
    : null;
  const previousBreadcrumbTooltip = previousBreadcrumbStep?.name
    ? `Back to ${previousBreadcrumbStep.name}`
    : 'Back';

  return (
    <div className="project-view-container">
      {/* Project Header */}
      <div 
        className="project-view-header"
        onClick={handleClearSelection}
      >
        <div className="project-view-header-left">
          <div className="project-view-header-top">
            <button className="back-btn" onClick={onBack} title="Back to Projects">
              <i className="fas fa-arrow-left"></i>
              Projects
            </button>
            <span className="project-role-badge">{project.UserRole}</span>
            <div className="project-meta-info">
              <span>Project ID: {project.Id}</span>
              {project.UserRoleId < 2 && (
                <span>Type: {project.Plan}</span>
              )}
              {project.UserRoleId < 2 && (
                <span></span>
              )}
            </div>
          </div>
          <div
            className="project-view-header-info"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="breadcrumb-path project-view-header-breadcrumb">
              {currentPath.map((pathItem, index) => (
                <React.Fragment key={pathItem.id}>
                  <span
                    className="breadcrumb-item"
                    onClick={(e) => handleBreadcrumbClick(index, e)}
                  >
                    {pathItem.name}
                  </span>
                  {index < currentPath.length - 1 && (
                    <i className="fas fa-chevron-right breadcrumb-separator" aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        
        <div className="project-actions-header">
          {(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') ? (
            /* RSA Existing & RSA Model: Analysis first (primary), then Files */
            <>
              <div className="analysis-btn-wrapper" ref={analysisBtnRef}>
                <button
                  className={`header-action-btn primary ${rsaViewMode === 'analysis' ? 'active' : ''}`}
                  onClick={() => {
                    setRsaViewMode('analysis');
                    updateRsaRouteState('analysis', routeRsaTab || (project.ProjectType === 'rsa-model' ? 'upload' : 'setup'));
                  }}
                  title="Analysis"
                >
                  <i className="fas fa-chart-line"></i>
                  Analysis
                </button>
                {showAnalysisFlyout && flyoutFixedStyle && (
                  <div
                    className="analysis-back-flyout"
                    style={{
                      position: 'fixed',
                      top: flyoutFixedStyle.top,
                      right: flyoutFixedStyle.right,
                      transform: 'translateY(-50%)',
                    }}
                  >
                    Click here to go back to Analysis Dashboard page
                    <i className="fas fa-arrow-right" aria-hidden />
                  </div>
                )}
              </div>
                <button
                  className={`header-action-btn secondary ${rsaViewMode === 'files' ? 'active' : ''}`}
                  onClick={() => {
                    setRsaViewMode('files');
                    updateRsaRouteState('files');
                    const el = document.getElementById('project-files-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  title="Project Files"
                >
                  <i className="fas fa-folder-open"></i>
                  Files
                </button>
              {headerMenuButton}
            </>
          ) : (
            /* Visualization: full header actions (3D View, Virtual Tour, etc.) */
            <>
              {canView && (
                <button 
                  className="header-action-btn primary"
                  onClick={async () => {
                    console.log(`🎯 3D View button clicked for project ${project.Id}!`);
                    try {
                      await viewerPresenceService.registerOverlayClick(project.Id);
                      console.log(`✅ Overlay click registered for project ${project.Id}`);
                    } catch (error) {
                      console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                    }
                    onViewProject(project);
                  }}
                >
                  <i className="fas fa-cube"></i>
                  3D View
                </button>
              )}
              {hasVirtualTour ? (
                <>
                  <VirtualTourButton project={project} />
                  <button className="header-action-btn tertiary">
                    <i className="fas fa-link"></i>
                    VTour Link
                  </button>
                </>
              ) : (
                <>
                  <button className="header-action-btn disabled">
                    <i className="fas fa-vr-cardboard"></i>
                    Virtual Tour
                  </button>
                  <button className="header-action-btn disabled">
                    <i className="fas fa-link"></i>
                    VTour Link
                  </button>
                </>
              )}
              {headerMenuButton}
            </>
          )}
        </div>
      </div>
      
      <ManageProjectUsersModal
        projectId={project.Id}
        projectName={displayProjectName}
        isOpen={showManageUsersModal}
        onClose={() => setShowManageUsersModal(false)}
      />

      <RenameProjectModal
        projectName={displayProjectName}
        initialName={displayProjectName}
        isOpen={showRenameModal}
        isSubmitting={isRenaming}
        error={renameError}
        onCancel={() => {
          if (isRenaming) {
            return;
          }
          setShowRenameModal(false);
          setRenameError('');
        }}
        onConfirm={handleRenameProject}
      />

      <LeaveProjectModal
        projectName={displayProjectName}
        isOpen={showLeaveProjectModal}
        isSubmitting={isLeavingProject}
        onCancel={() => {
          if (isLeavingProject) {
            return;
          }
          setShowLeaveProjectModal(false);
        }}
        onConfirm={handleLeaveProject}
      />

      {/* Project Content */}
      {(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') && rsaViewMode === 'analysis' ? (
        <div
          className={`project-content rsa-analysis-content ${project.ProjectType === 'rsa-model' ? 'rsa-model-content' : ''}`}
          data-project-type={project.ProjectType}
        >
          {project.ProjectType === 'rsa-model' ? (
            <RSAModelAnalysis
              projectId={project.Id}
              preloadedRsaSetup={preloadedRsaSetup}
              preloadedRsaSetupFolderId={preloadedRsaSetupFolderId}
              preloadedRsaSetupFolderName={preloadedRsaSetupFolderName}
              preloadRsaSetupLoading={preloadRsaSetupLoading}
              initialActiveMainTab={routeRsaTab || 'upload'}
              onActiveMainTabChange={handleActiveMainTabChange}
              onGoToFiles={handleGoToFiles}
            />
          ) : (
            <RSAExistingAnalysis
              projectId={project.Id}
              preloadedRsaSetup={preloadedRsaSetup}
              preloadedRsaSetupFolderId={preloadedRsaSetupFolderId}
              preloadedRsaSetupFolderName={preloadedRsaSetupFolderName}
              preloadRsaSetupLoading={preloadRsaSetupLoading}
              initialActiveMainTab={routeRsaTab || 'upload'}
              onActiveMainTabChange={handleActiveMainTabChange}
              onGoToFiles={handleGoToFiles}
            />
          )}
        </div>
      ) : (
        <div
          id={(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') ? 'project-files-section' : undefined}
          className="project-content"
        >
          {/* Project Data Section */}
          {projectData && (
            <div className="project-data-section">
              <h3>Project Data</h3>
              <div className="project-data-grid">
                <div className="data-item">
                  <span className="data-label">Project ID:</span>
                  <span className="data-value">{projectData.project_id}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Project Code:</span>
                  <span className="data-value">{projectData.project_code}</span>
                </div>
                {projectData.files && projectData.files.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Files:</span>
                    <span className="data-value">{projectData.files.length}</span>
                  </div>
                )}
                {projectData.tasks && projectData.tasks.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Tasks:</span>
                    <span className="data-value">{projectData.tasks.length}</span>
                  </div>
                )}
                {projectData.reports && projectData.reports.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Reports:</span>
                    <span className="data-value">{projectData.reports.length}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="project-tree-panel">
              <FileExplorer 
              ref={fileExplorerRef}
              project={project}
              onFileSelect={handleFileSelect}
              onFolderSelect={handleFolderSelect}
              currentPath={currentPath}
              onBreadcrumbUpdate={updateBreadcrumb}
              selectedFolder={selectedFolder}
              onClearSelection={handleClearSelection}
              showUploadModal={showUploadModal}
              onOpenUploadModal={() => setShowUploadModal(true)}
              onCloseUploadModal={() => setShowUploadModal(false)}
              currentFolderId={selectedFolder && selectedFolder.id !== 'root' ? selectedFolder.id : project.Id}
              fileListToolbar={
                <div className="breadcrumb-actions">
                  {previousBreadcrumbStep && (
                    <button
                      type="button"
                      className="file-action-btn file-action-btn-back"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBreadcrumbClick(currentPath.length - 2, e);
                      }}
                      title={previousBreadcrumbTooltip}
                      aria-label={previousBreadcrumbTooltip}
                    >
                      <i className="fas fa-arrow-left" aria-hidden />
                    </button>
                  )}
                  <button 
                    className="file-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefresh();
                    }}
                    title="Refresh"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                  <PermissionGate group="Admin" fallback={null}>
                    {isRsaProject && (
                      <button
                        type="button"
                        className="file-action-btn rsa-resync-cache-btn"
                        onClick={handleRsaResyncSourceCaches}
                        disabled={rsaResyncLoading}
                        title="Resync RSA mileage &amp; ISD MySQL caches from current Source → RSA folders (developers)"
                      >
                        <i
                          className={`fas fa-database${rsaResyncLoading ? ' fa-spin' : ''}`}
                          aria-hidden
                        />
                      </button>
                    )}
                    </PermissionGate>
                    <button 
                      className="file-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNewFolder();
                      }}
                      title="New Folder"
                    >
                      <i className="fas fa-folder-plus"></i>
                    </button>
                  <button 
                    className="file-action-btn upload-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUploadModal(true);
                    }}
                    title="Upload"
                  >
                    <i className="fas fa-upload"></i>
                  </button>
                  {canDeleteSelection && (
                    <button
                      type="button"
                      className="file-action-btn file-action-btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExplorerDelete();
                      }}
                      title="Delete"
                    >
                      <i className="fas fa-trash" aria-hidden />
                    </button>
                  )}
                  {hasSelectedFiles && (
                    <button 
                      className="file-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                      title="Download"
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  )}
                </div>
              }
            />
          </div>
        </div>
      )}

    </div>
  );
};

// Virtual Tour Button Component
const VirtualTourButton = ({ project }) => {
  const [virtualTourCode, setVirtualTourCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load virtual tour code on component mount
  useEffect(() => {
    const loadVirtualTourCode = async () => {
      try {
        const code = await getVirtualTourCode(project.Id);
        setVirtualTourCode(code);
      } catch (error) {
        console.error('Error loading virtual tour code:', error);
      }
    };
    
    loadVirtualTourCode();
  }, [project.Id]);

  const handleVirtualTourClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const virtualTourUrl = await getVirtualTourUrl(project.Id);
      console.log('🔗 Opening virtual tour URL:', virtualTourUrl);
      window.open(virtualTourUrl, '_blank');
    } catch (error) {
      console.error('Error getting virtual tour URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button 
      className="header-action-btn tertiary"
      onClick={handleVirtualTourClick}
      disabled={isLoading}
      title={virtualTourCode ? `Virtual Tour (${virtualTourCode})` : 'Virtual Tour'}
    >
      <i className="fas fa-vr-cardboard"></i>
      {isLoading ? 'Loading...' : `VTOUR ${virtualTourCode ? `(${virtualTourCode})` : ''}`}
    </button>
  );
};

export default ProjectView;
